import * as React from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useAuth, ApiError } from '@/lib/auth';
import { isCognitoEnabled } from '@/lib/cognito';
import { Button, PasswordInput } from '@/components/ui';
import { useI18n } from '@/lib/i18n';
import logoUrl from '@/assets/suzuki-logo.png';

export function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const emailParam = searchParams.get('email') || '';
  const codeParam = searchParams.get('code') || '';
  const { resetPassword } = useAuth();
  const { t } = useI18n();

  const cognitoMode = isCognitoEnabled();
  const minLength = 8;

  const [email, setEmail] = React.useState(emailParam);
  const [code, setCode] = React.useState(codeParam);
  const [password, setPassword] = React.useState('');
  const [confirmPassword, setConfirmPassword] = React.useState('');
  const [error, setError] = React.useState('');
  const [success, setSuccess] = React.useState(false);
  const [loading, setLoading] = React.useState(false);

  const strengthOk = password.length >= minLength
    && /[A-Z]/.test(password)
    && /[a-z]/.test(password)
    && /\d/.test(password)
    && (!cognitoMode || /[^A-Za-z0-9]/.test(password));

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError('');

    if (password !== confirmPassword) {
      setError(t('auth.passwordsDoNotMatch'));
      return;
    }
    if (!strengthOk) {
      setError(t('auth.passwordMinimum', { count: minLength }));
      return;
    }

    if (cognitoMode) {
      if (!email) { setError(t('auth.emailRequired')); return; }
      if (!code) { setError(t('auth.codeRequired')); return; }
    } else {
      if (!token) { setError(t('auth.missingToken')); return; }
    }

    setLoading(true);
    try {
      if (cognitoMode) {
        await resetPassword(email, code, password);
      } else {
        await resetPassword(token!, password);
      }
      setSuccess(true);
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message || t('auth.failedReset'));
      } else if (err instanceof Error) {
        setError(err.message);
      } else {
        setError(t('common.unexpectedError'));
      }
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="flex min-h-screen flex-col bg-surface-page">
      <div className="h-1.5 bg-brand-600" aria-hidden="true" />
      <div className="flex flex-1 items-center justify-center px-4">
        <div className="w-full max-w-md space-y-8 rounded-xl border border-line bg-surface p-8 shadow-raised">
          <div className="text-center">
            <img src={logoUrl} alt="Suzuki" className="mx-auto mb-4 h-12" />
            <h1 className="text-2xl font-bold text-ink">{t('auth.setNewPassword')}</h1>
            {cognitoMode && (
              <p className="mt-2 text-sm text-ink-muted">{t('auth.enterCodeFromEmail')}</p>
            )}
          </div>

          {success ? (
            <div className="space-y-4 text-center">
              <div className="rounded-xl border border-success-500/20 bg-success-50 p-4 text-sm text-success-700" role="status">
                {t('auth.resetSuccess')}
              </div>
              <Link
                to="/login"
                className="inline-block rounded-lg bg-brand-600 px-4 py-2 text-sm font-semibold text-white shadow-card transition-all duration-150 hover:bg-brand-700 hover:shadow-raised focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-2"
              >
                {t('auth.goToLogin')}
              </Link>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="mt-8 space-y-6" noValidate>
              {error && (
                <div className="rounded-xl border border-accent-100 bg-accent-50 p-3 text-sm text-accent-700" role="alert">
                  {error}
                </div>
              )}

              {!cognitoMode && !token && (
                <div className="rounded-xl border border-warning-500/30 bg-warning-50 p-3 text-sm text-warning-700" role="alert">
                  {t('auth.missingResetToken')}
                </div>
              )}

              <div className="space-y-4">
                {cognitoMode && (
                  <>
                    <div>
                      <label htmlFor="email" className="block text-sm font-medium text-ink-muted">
                        {t('auth.emailAddress')}
                      </label>
                      <input
                        id="email"
                        type="email"
                        required
                        autoComplete="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        className="mt-1 block w-full rounded-lg border border-line-strong px-3 py-2 shadow-card transition-all duration-150 hover:border-brand-300 focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:ring-offset-1"
                      />
                    </div>
                    <div>
                      <label htmlFor="code" className="block text-sm font-medium text-ink-muted">
                        {t('auth.verificationCode')}
                      </label>
                      <input
                        id="code"
                        type="text"
                        inputMode="numeric"
                        required
                        autoComplete="one-time-code"
                        value={code}
                        onChange={(e) => setCode(e.target.value)}
                        className="mt-1 block w-full rounded-lg border border-line-strong px-3 py-2 shadow-card transition-all duration-150 hover:border-brand-300 focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:ring-offset-1"
                        placeholder={t('auth.codeFromEmail')}
                      />
                    </div>
                  </>
                )}

                <div>
                  <label htmlFor="password" className="block text-sm font-medium text-ink-muted">
                    {t('auth.newPassword')}
                  </label>
                  <div className="mt-1">
                    <PasswordInput
                      id="password"
                      required
                      autoComplete="new-password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="block w-full rounded-lg border border-line-strong px-3 py-2 shadow-card transition-all duration-150 hover:border-brand-300 focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:ring-offset-1"
                    />
                  </div>
                  <div className="mt-2 h-1.5 w-full rounded-full bg-surface-subtle" role="progressbar" aria-valuenow={password.length} aria-valuemax={minLength}>
                    <div
                      className={`h-1.5 rounded-full transition-all ${
                        password.length === 0
                          ? 'w-0'
                          : strengthOk
                            ? 'w-full bg-success-500'
                            : 'w-1/2 bg-warning-500'
                      }`}
                    />
                  </div>
                  <p className="mt-1 text-xs text-ink-subtle" aria-live="polite">
                    {password.length > 0 && !strengthOk
                      ? t('auth.passwordFormat', { count: minLength })
                      : null}
                    {strengthOk ? t('auth.looksGood') : null}
                  </p>
                </div>

                <div>
                  <label htmlFor="confirm" className="block text-sm font-medium text-ink-muted">
                    {t('auth.confirmPassword')}
                  </label>
                  <div className="mt-1">
                    <PasswordInput
                      id="confirm"
                      required
                      autoComplete="new-password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      className="block w-full rounded-lg border border-line-strong px-3 py-2 shadow-card transition-all duration-150 hover:border-brand-300 focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:ring-offset-1"
                    />
                  </div>
                </div>
              </div>

              <Button type="submit" disabled={loading || (!cognitoMode && !token)} className="w-full">
                {loading ? t('auth.resetting') : t('auth.resetPassword')}
              </Button>

              <div className="text-center">
                <Link to="/login" className="text-sm font-medium text-brand-700 transition-colors hover:text-brand-800 hover:underline">
                  {t('common.backToLogin')}
                </Link>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
